import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { MsalProvider } from '@azure/msal-react';
import theme from './theme/theme';
import { msalInstance } from './config/msalInstance';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfigProvider, useConfig } from './contexts/ConfigContext';
import { EventProvider } from './contexts/EventContext';
import { VolunteerProvider } from './contexts/VolunteerContext';
import { ServiceWorkerProvider } from './contexts/ServiceWorkerContext';
import { MainLayout, AuthLayout } from './layouts';
import { LoadingSpinner, UpdateNotification, PWASessionRecorder } from './components/common';
import { HomePage, LoginPage, MagicLinkPage, SsoCallbackPage } from './pages/public';
import { EventsPage, EventDetailPage, MyRegistrationsPage, MyGuestsPage, FamilyMembersPage, ProfilePage, FeedbackPage, SupportPage } from './pages/family';
import {
  AdminDashboardPage,
  ManageEventsPage,
  CreateEventPage,
  EditEventPage,
  AttendeesPage,
  EventRegisterPage,
  ReportsPage,
  ManageLocationsPage,
  ManageCategoriesPage,
  CheckInPage,
  QRCheckInPage,
  NotificationsPage,
  NotificationLogPage,
  SettingsPage,
} from './pages/admin';
import {
  VolunteersLandingPage,
  VolunteerShiftsPage,
  VolunteerShiftDetailPage,
  VolunteerMyShiftsPage,
  VolunteerProfilePage,
  VolunteerHomePage,
} from './pages/volunteers';
import {
  ManageVolunteersPage,
  ManageVolunteerShiftsPage,
  CreateVolunteerShiftPage,
  EditVolunteerShiftPage,
  VolunteerShiftAttendeesPage,
  VolunteerReportsPage,
} from './pages/admin/volunteers';
import { ROUTES } from './utils/constants';
import { isVolunteerOnly } from './utils/helpers';

// Protected route wrapper
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};

// Auth route wrapper (redirect if already logged in)
interface AuthRouteProps {
  children: React.ReactNode;
}

const AuthRoute: React.FC<AuthRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};

// Redirect volunteer-only users away from family routes
const FamilyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (isVolunteerOnly(user)) {
    return <Navigate to={ROUTES.VOLUNTEERS} replace />;
  }
  return <>{children}</>;
};

// Show volunteer home or family home based on user type
const HomePageRouter: React.FC = () => {
  const { user } = useAuth();
  return isVolunteerOnly(user) ? <VolunteerHomePage /> : <HomePage />;
};

const AppRoutes: React.FC = () => {
  const { features } = useConfig();
  return (
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route
          path={ROUTES.LOGIN}
          element={
            <AuthRoute>
              <LoginPage />
            </AuthRoute>
          }
        />
        <Route path={ROUTES.MAGIC_LINK} element={<MagicLinkPage />} />
        <Route path={ROUTES.SSO_CALLBACK} element={<SsoCallbackPage />} />
      </Route>

      {/* Main app routes */}
      <Route element={<MainLayout />}>
        {/* Protected family routes */}
        <Route
          path={ROUTES.HOME}
          element={
            <ProtectedRoute>
              <HomePageRouter />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.EVENTS}
          element={
            <ProtectedRoute>
              <FamilyRoute>
                <EventsPage />
              </FamilyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.EVENT_DETAIL}
          element={
            <ProtectedRoute>
              <FamilyRoute>
                <EventDetailPage />
              </FamilyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.MY_REGISTRATIONS}
          element={
            <ProtectedRoute>
              <FamilyRoute>
                <MyRegistrationsPage />
              </FamilyRoute>
            </ProtectedRoute>
          }
        />
        {/* Plan 4.13 — My Guests route. The route exists unconditionally;
            the backend `/families/:familyId/guests` endpoint returns 404
            when the eventGuests feature flag is off (anti-enumeration),
            so the page just renders an empty state. */}
        <Route
          path={ROUTES.MY_GUESTS}
          element={
            <ProtectedRoute>
              <FamilyRoute>
                <MyGuestsPage />
              </FamilyRoute>
            </ProtectedRoute>
          }
        />
        {/* D8 — Family Members self-service. Mounted unconditionally; the
            backend `/families/:familyId/members` endpoints 404 when the
            selfAddMembers flag is off (anti-enumeration). Discoverability
            (the Profile card) is gated on config.features.selfAddMembers. */}
        <Route
          path={ROUTES.FAMILY_MEMBERS}
          element={
            <ProtectedRoute>
              <FamilyRoute>
                <FamilyMembersPage />
              </FamilyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PROFILE}
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.FEEDBACK}
          element={
            <ProtectedRoute>
              <FamilyRoute>
                <FeedbackPage />
              </FamilyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.FEEDBACK_EVENT}
          element={
            <ProtectedRoute>
              <FamilyRoute>
                <FeedbackPage />
              </FamilyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SUPPORT}
          element={
            <ProtectedRoute>
              <SupportPage />
            </ProtectedRoute>
          }
        />

        {/* Protected volunteer routes (all authenticated users) */}
        <Route
          path={ROUTES.VOLUNTEERS}
          element={
            <ProtectedRoute>
              <VolunteersLandingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.VOLUNTEER_SHIFTS}
          element={
            <ProtectedRoute>
              <VolunteerShiftsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.VOLUNTEER_SHIFT_DETAIL}
          element={
            <ProtectedRoute>
              <VolunteerShiftDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.VOLUNTEER_MY_SHIFTS}
          element={
            <ProtectedRoute>
              <VolunteerMyShiftsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.VOLUNTEER_PROFILE}
          element={
            <ProtectedRoute>
              <VolunteerProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Protected admin routes */}
        <Route
          path={ROUTES.ADMIN_DASHBOARD}
          element={
            <ProtectedRoute requireAdmin>
              <AdminDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_EVENTS}
          element={
            <ProtectedRoute requireAdmin>
              <ManageEventsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_CREATE_EVENT}
          element={
            <ProtectedRoute requireAdmin>
              <CreateEventPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_EDIT_EVENT}
          element={
            <ProtectedRoute requireAdmin>
              <EditEventPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_ATTENDEES}
          element={
            <ProtectedRoute requireAdmin>
              <AttendeesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_EVENT_ATTENDEES}
          element={
            <ProtectedRoute requireAdmin>
              <AttendeesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_EVENT_REGISTER}
          element={
            <ProtectedRoute requireAdmin>
              <EventRegisterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_REPORTS}
          element={
            <ProtectedRoute requireAdmin>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_LOCATIONS}
          element={
            <ProtectedRoute requireAdmin>
              <ManageLocationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_CATEGORIES}
          element={
            <ProtectedRoute requireAdmin>
              <ManageCategoriesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_CHECK_IN}
          element={
            <ProtectedRoute requireAdmin>
              <CheckInPage />
            </ProtectedRoute>
          }
        />
        {/* QR check-in is retired when the qrCheckIn feature flag is off —
            redirect to the dashboard (manual check-in remains on each event). */}
        <Route
          path={ROUTES.ADMIN_QR_CHECK_IN}
          element={
            features.qrCheckIn ? (
              <ProtectedRoute requireAdmin>
                <QRCheckInPage />
              </ProtectedRoute>
            ) : (
              <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />
            )
          }
        />
        <Route
          path={ROUTES.ADMIN_NOTIFICATIONS}
          element={
            <ProtectedRoute requireAdmin>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_NOTIFICATION_LOG}
          element={
            <ProtectedRoute requireAdmin>
              <NotificationLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_SETTINGS}
          element={
            <ProtectedRoute requireAdmin>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Admin volunteer routes */}
        <Route
          path={ROUTES.ADMIN_VOLUNTEERS}
          element={
            <ProtectedRoute requireAdmin>
              <ManageVolunteersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_VOLUNTEER_SHIFTS}
          element={
            <ProtectedRoute requireAdmin>
              <ManageVolunteerShiftsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_VOLUNTEER_SHIFT_CREATE}
          element={
            <ProtectedRoute requireAdmin>
              <CreateVolunteerShiftPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_VOLUNTEER_SHIFT_EDIT}
          element={
            <ProtectedRoute requireAdmin>
              <EditVolunteerShiftPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_VOLUNTEER_SHIFT_DETAIL}
          element={
            <ProtectedRoute requireAdmin>
              <VolunteerShiftAttendeesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ADMIN_VOLUNTEER_REPORTS}
          element={
            <ProtectedRoute requireAdmin>
              <VolunteerReportsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <MsalProvider instance={msalInstance}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ServiceWorkerProvider>
          <ConfigProvider>
            <AuthProvider>
              <EventProvider>
                <VolunteerProvider>
                  <BrowserRouter>
                    <PWASessionRecorder />
                    <AppRoutes />
                    <UpdateNotification />
                  </BrowserRouter>
                </VolunteerProvider>
              </EventProvider>
            </AuthProvider>
          </ConfigProvider>
        </ServiceWorkerProvider>
      </ThemeProvider>
    </MsalProvider>
  );
};

export default App;
